import { getActivePinia, type Store } from 'pinia';
import { useOrgStore } from '@/stores/organisations.store';
import { useOrdersStore } from '@/stores/orders.store';
import { usePusherStore } from '@/stores/pusher.store';

/**
 * Stores that project external state rather than hold user or organisation data, and so must
 * survive both logout and an organisation switch.
 *
 * Context: `feature-flags` mirrors LaunchDarkly, which `App.vue` pushes in with a `watch` that
 * only fires on change (PE-614). Resetting the mirror does not re-request the flag — the LD ref
 * still holds the same value, so the watch stays quiet and the mirror is stuck at its default
 * for the rest of the session, silently closing every surface the flag gates.
 */
const STORES_EXEMPT_FROM_RESET = new Set(['feature-flags']);

/**
 * Calls `$reset()` on every store in `storesMap` except those `exclude` names, and keeps going
 * when one throws.
 *
 * Pinia defines `$reset` on every store, so `typeof store.$reset === 'function'` cannot tell a
 * store that implements one from a setup store that does not — for the latter, a non-production
 * build supplies a stub that throws. Guarding each call individually is what stops one such
 * store from taking down the whole reset, and the flow that asked for it.
 */
function resetStores(storesMap: Map<string, Store>, exclude: Set<string>): void {
  storesMap.forEach((store: Store) => {
    if (exclude.has(store.$id) || STORES_EXEMPT_FROM_RESET.has(store.$id)) {
      return;
    }

    try {
      store.$reset();
    } catch (error) {
      console.warn(`Could not reset store "${store.$id}":`, error);
    }
  });
}

/**
 * Utility function to reset all Pinia stores to their initial state
 */
export function clearAllPiniaStores(): boolean {
  try {
    const pinia = getActivePinia();

    if (!pinia) {
      console.warn('No active Pinia instance found');
      return false;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const storesMap = (pinia as any)._s as Map<string, Store>;

    if (!storesMap) {
      console.warn('Could not access stores map');
      return false;
    }

    // `$reset` restores state only, so a list read still in flight would write into the reset store.
    useOrdersStore().invalidateListReads();
    useOrgStore().invalidatePaymentRequestReads();
    resetStores(storesMap, new Set());

    return true;
  } catch (error) {
    console.error('Failed to reset Pinia stores:', error);
    return false;
  }
}

export function clearStoresForOrganisationSwitch() {
  const stores = getActivePinia();
  const orgStore = useOrgStore();

  orgStore.clearStore();
  useOrdersStore().invalidateListReads();

  if (stores) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const storesMap = (stores as any)._s as Map<string, Store>;

    // Identity outlives the switch — the same user, with the same roles and preferences, is
    // still signed in. `userback` is here because the widget is torn down on logout instead.
    // `pusher` holds the live socket and its channels, which the switch does not close: resetting
    // it would orphan them, still bound and delivering, with nothing left to unsubscribe them.
    const storesToExclude = new Set([
      'auth',
      'roles',
      'organisations',
      'user-preferences',
      'permissions',
      'userback',
      'pusher',
    ]);

    resetStores(storesMap, storesToExclude);
    usePusherStore().clearForOrganisationSwitch();
  }
}
